import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

const UPDATED = "June 2026";

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-heading text-lg font-bold text-[#101828]">{title}</h2>
      <div className="mt-2 space-y-2 text-sm text-[#475467] leading-relaxed">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12" data-testid="privacy-page">
      <div className="flex items-center gap-2 text-[#0D5C75]">
        <ShieldCheck size={22} />
        <span className="font-heading font-bold uppercase tracking-wide text-xs">Legal</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#101828] mt-2">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mt-2">Last updated: {UPDATED}</p>

      <Section title="1. Who We Are">
        <p>SarvaSeva is a technology platform that connects patients with independent, accredited healthcare providers in India. We are the data controller for the personal information you provide to us. We are not a hospital, clinic, or medical practice.</p>
      </Section>

      <Section title="2. Information We Collect">
        <p>We collect: (a) account data (name, email, country); (b) health information you choose to share (medical history, diagnoses, treatment preferences, allergies); (c) transaction and booking records; and (d) technical data (device, browser, and a one-way hashed IP address for security and consent records).</p>
      </Section>

      <Section title="3. How We Use Your Information">
        <p>We process your data to provide clinical matchmaking, coordinate bookings with your chosen provider, process escrow-protected payments, and comply with legal obligations. We do not sell, rent, or share your personal or health information with third parties for marketing purposes.</p>
      </Section>

      <Section title="4. Legal Bases (GDPR / DPDP Act)">
        <p>We rely on your explicit consent to process health data (special-category / sensitive personal data), on the performance of a contract to deliver our services, and on legitimate interests and legal obligations to secure the platform and retain financial records.</p>
      </Section>

      <Section title="5. Health Data & Encryption">
        <p>Sensitive health information (medical history, diagnoses, treatment preferences, allergies) is encrypted at rest using AES-256 and in transit using TLS. Health data is shared only with the specific healthcare provider you select, for treatment coordination.</p>
      </Section>

      <Section title="6. Cookies">
        <p>We use essential cookies to run the platform and, with your consent, analytics cookies to improve it. You can choose "Essential Only" or "Accept All" in our cookie banner, and change your choice at any time by clearing your cookies.</p>
      </Section>

      <Section title="7. Your Rights">
        <p>Under GDPR and the Indian DPDP Act you have the right to access, correct, and delete your data, to withdraw consent, and to data portability. You can request erasure at any time from your account settings; we will anonymize your personal data while retaining financial records required by law.</p>
      </Section>

      <Section title="8. Data Retention">
        <p>We retain personal data only as long as necessary to provide our services. When you request deletion, we permanently anonymize identifying information; anonymized transaction records may be retained to meet accounting, tax, and anti-fraud obligations.</p>
      </Section>

      <Section title="9. Payment Security (PCI DSS)">
        <p>Payments are processed by our PCI-DSS-compliant payment partner. We never store your full card details on our servers.</p>
      </Section>

      <Section title="10. Contact">
        <p>For privacy requests or questions, contact our Data Protection team at privacy@sarvaseva.com. See also our <Link to="/terms" className="text-[#0D5C75] underline font-medium">Terms of Service</Link>.</p>
      </Section>

      <p className="mt-10 rounded-xl bg-[#FFFBEB] border border-[#D97706]/30 p-4 text-xs text-[#92400E]" data-testid="privacy-legal-review-note">
        This policy is a template and has not been reviewed by legal counsel. It will be replaced with lawyer-reviewed copy before public launch.
      </p>
    </div>
  );
}
